// src/app/register/register-component.ts
import { Component, inject, PLATFORM_ID, signal, OnInit, LOCALE_ID } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { MobileInput } from './mobile-input/mobile-input';
import { MobileValue } from './mobile-input/mobile-input.model';
import { AuthService } from '../../../../services/auth-service';
import { Router, RouterLink } from '@angular/router';
import { CustomValidators } from '../../../../validators/custom-validators';
import { CreateUserDTO } from '../../../../services/auth-service.model';
import { Error } from '../shared/error/error';
import { ImageSide } from '../shared/image-side/image-side';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';

@Component({
  selector: 'app-register-component',
  standalone: true,
  imports: [ReactiveFormsModule, MobileInput, CommonModule, RouterLink, Error, ImageSide, SuccessfulNotification],
  templateUrl: './register-component.html',
  styleUrls: ['./register-component.scss'],
})
export class RegisterComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private locale = inject(LOCALE_ID);
  currentLocale = signal<string>('en');
  isNotificationVisible = signal(false);

  showGlobalError: boolean = false;
  showPassword = false;
  showConfirmPassword = false;
  apiError = signal('');
  loading = signal<boolean>(false);

  registerForm = new FormGroup(
    {
      firstName: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.pattern(/^(?! )[A-Za-z\u0600-\u06FF]+( [A-Za-z\u0600-\u06FF]+)*$/),
      ]),
      lastName: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.pattern(/^(?! )[A-Za-z\u0600-\u06FF]+( [A-Za-z\u0600-\u06FF]+)*$/),
      ]),
      email: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(150),
        // Validators.email,
        Validators.pattern(/^(?!.*@xn--)[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/),

        // Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ]),
      mobileNumber: new FormControl<MobileValue | null>(null, [Validators.required, CustomValidators.mobileNumber()]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(50),
        CustomValidators.strongPassword(),
      ]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    { validators: CustomValidators.passwordMatch('password', 'confirmPassword') },
  );

  ngOnInit() {
    this.currentLocale.set(this.locale.startsWith('ar') ? 'ar' : this.locale.startsWith('en') ? 'en' : 'en');
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem('registerForm');
      if (saved) {
        const values = JSON.parse(saved);
        this.registerForm.patchValue(values);
      }
      this.registerForm.valueChanges.subscribe((value) => {
        const { password, confirmPassword, ...toSave } = value;
        localStorage.setItem('registerForm', JSON.stringify(toSave));
      });
    }
  }

  onSubmit() {
    console.log('Form submitted with value:', this.registerForm);
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }
    this.apiError.set('');
    this.loading.set(true);

    const formValue = this.registerForm.value;
    const mobileControlValue = this.registerForm.get('mobileNumber')!.value!;
    let fullMobileNumber = '';
    if (mobileControlValue.number.startsWith('0') && mobileControlValue.number.length === 11) {
      fullMobileNumber = mobileControlValue.dialCode + mobileControlValue.number.slice(1);
    }
    if (!mobileControlValue.number.startsWith('0') && mobileControlValue.number.length === 10) {
      fullMobileNumber = mobileControlValue.dialCode + mobileControlValue.number;
    }

    const { confirmPassword, ...payload } = formValue;
    if (payload.email?.trim() === '') {
      payload.email = null;
    }

    const registerPayload: CreateUserDTO = {
      firstName: payload.firstName!,
      lastName: payload.lastName!,
      email: payload.email!,
      password: payload.password!,
      mobileNumber: fullMobileNumber,
    };

    this.authService
      .registerUser(registerPayload)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.isNotificationVisible.set(true);
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 1000);
        },
        error: (err) => {
          if (this.locale === 'ar') {
            this.apiError.set(err.messageAr || 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى لاحقًا.');
            return;
          }
          if (this.locale === 'en') {
            this.apiError.set(err.messageEn || 'An unexpected error occurred. Please try again later.');
            return;
          }
        },
      });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.registerForm.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  hasError(fieldName: string, errorName: string): boolean {
    return !!(this.registerForm.get(fieldName)?.hasError(errorName) && this.registerForm.get(fieldName)?.touched);
  }

  isConfirmPasswordMismatch(): boolean {
    return !!(this.registerForm.hasError('passwordMismatch') && this.registerForm.get('confirmPassword')?.touched);
  }

  private errorCodeToMessage(code: string): string {
    if (code === '0010') {
      return 'This email/mobile number is already registered. Please use a different one or log in instead.';
    } else {
      return 'An unexpected error occurred. Please try again later.';
    }
  }
  isArabic() {
    return this.locale.startsWith('ar');
  }
  isEnglish() {
    return this.locale.startsWith('en');
  }
}
